'use client';

import { Logo } from '@/shared/ui/brand/Logo';
import { ProjectActionsMenu } from './ProjectActionsMenu';
import { FeedbackHeaderButton } from '@/features/support/ui/FeedbackHeaderButton';
import { ProjectSwitcher } from './ProjectSwitcher';
import { BuildingPackTag } from './BuildingPackTag';
import { useProjects } from '../hooks/use-projects';

interface ProjectHeroSectionProps {
  projectName: string;
  projectId?: string;
  // Center the hero title (+ switcher chevron) horizontally. Set only in the
  // onboarding empty state, where the placeholder below is centered; left flag
  // (default) once the building has content.
  isCentered?: boolean;
}

export function ProjectHeroSection({
  projectName,
  projectId,
  isCentered = false,
}: ProjectHeroSectionProps) {
  // Per-building pack marking on the building's own page (mirrors the storefront
  // list). Pack state rides on the already-loaded projects (server-computed,
  // never derived from purchases). The marking is shown ONLY for an active
  // Kit/Pro Plan pack — a positive status badge / reward. The no-pack ("Free")
  // state is deliberately UNMARKED on the building hero: a fresh building reads
  // as "just my house", with zero upgrade nag in the activation funnel. Users
  // who want to buy reach the pack paywall from the Account page. (The shared
  // BuildingPackTag still renders the muted "Free" state in the Packs storefront
  // list, where the marking is purely informational.)
  const { data: projectsResponse } = useProjects();
  const pack = projectsResponse?.data.find((p) => p.id === projectId)?.attributes
    .pack;
  const isPackActive = pack?.is_active === true;

  return (
    // Header of the app home (mirrors the Apple `ProjectHeroSection`): the
    // GetFacade wordmark, then a large leading title. No house photo, no
    // subtitle and no background wash — the page surface runs edge to edge.
    <header className="relative w-full">
      <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Wordmark row. One wordmark height (24px) of air above it — the same
            top inset the Apple / Android headers use. The building page is the
            app home, so there is no back button: only the trailing feedback and
            ⋮ (rename / delete / create-building) controls; the building
            switcher lives in the title below. */}
        <div className="flex items-center justify-between gap-2 pt-6 min-h-9">
          {/* Brand wordmark on the app home, at a FIXED 146x24 (the asset's
              3378:554 ratio at 24px tall) so it never scales with the layout —
              same size on Apple / Android. Two assets instead of a tint: the
              SVG ships its own color, so light gets the brand teal and dark
              the brighter Teal400. */}
          <Logo width={146} height={24} className="dark:hidden" />
          <Logo variant="dark" width={146} height={24} className="hidden dark:block" />
          <div className="flex items-center gap-2">
            <FeedbackHeaderButton variant="hero" />
            <ProjectActionsMenu variant="hero" />
          </div>
        </div>

        {/* Large leading title (dark), aligned with the content sections below.
            When there is more than one building the title doubles as the
            building switcher (title + up/down chevron); a single building shows
            a plain title. Mirrors the Apple hero. */}
        <ProjectSwitcher
          currentProjectId={projectId}
          currentProjectName={projectName}
          isCentered={isCentered}
        />

        {/* Pack marking for this building — only the active Kit/Pro Plan badge.
            The no-pack "Free" state is intentionally unmarked here (see comment
            above); the purchase funnel lives on the Account page. */}
        {isPackActive && (
          <div className="-mt-2 flex flex-wrap items-center gap-3 pb-6">
            <BuildingPackTag pack={pack} />
          </div>
        )}
      </div>
    </header>
  );
}
